import React from 'react';
import {
  ClassScheduleSettings,
  getGradeTrackSections,
  loadGradeTrackSectionsCache,
  type PageSectionLayout,
} from '../services/sectionLayoutService';
import { filterTracksSubscribedToGradeTrackSections } from '../utils/gradeTrackSectionSubscription';
import { AcademicTerm, AnnualTimetableCell, CalendarDay, Grade, Track } from '../types/domain';
import { buildAnnualCellMap, buildAnnualClassEntries } from '../utils/annualTimetable';
import { saveProgramProgressDataCache } from '../utils/programProgressDataCache';
import { useAnnualProgressMetaQuery } from '../query/hooks/useAnnualProgressMetaQuery';
import { useAnnualTimetableCellsQuery } from '../query/hooks/useAnnualTimetableCellsQuery';
import { useCalendarDaysQuery } from '../query/hooks/useCalendarDaysQuery';
import { useSchoolScopedCacheSyncReady } from './useSchoolScopedCacheSyncReady';

interface UseProgramProgressDataParams {
  workspaceId: string;
  grades: Grade[];
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  /** keep-alive 비활성 시 fetch effect 생략 */
  isActive?: boolean;
}

export interface ProgramProgressDataState {
  loading: boolean;
  error: string | null;
  terms: AcademicTerm[];
  schedule: ClassScheduleSettings | null;
  tracks: Track[];
  gradeTrackSections: PageSectionLayout[];
  regularColors: string[];
  dedicatedColors: string[];
  baseExcludeColors: string[];
  holidaySet: Set<string>;
  cellsByKey: Record<string, AnnualTimetableCell>;
  /** 로그인·새로고침 시 workspaces.updated_at sync 완료 여부 — 하위 진도/스코프 조회 게이트용 */
  schoolScopedCacheSyncReady: boolean;
}

export function useProgramProgressData(params: UseProgramProgressDataParams): ProgramProgressDataState {
  const { workspaceId, grades, gradeId, academicYear, termNumber, isActive = true } = params;
  const { schoolScopedCacheSyncReady, schoolScopedCacheSyncPending } = useSchoolScopedCacheSyncReady(
    workspaceId,
    isActive
  );
  const enabled =
    isActive && schoolScopedCacheSyncReady && !!workspaceId.trim() && !!gradeId.trim();
  const [gradeTrackSections, setGradeTrackSections] = React.useState<PageSectionLayout[]>(() => {
    if (workspaceId) {
      const fromSectionCache = loadGradeTrackSectionsCache(workspaceId);
      if (fromSectionCache != null) return fromSectionCache;
    }
    return [];
  });

  const metaQuery = useAnnualProgressMetaQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    enabled,
  });

  const terms: AcademicTerm[] = metaQuery.data?.terms ?? [];
  const schedule = metaQuery.data?.schedule ?? null;
  const tracks: Track[] = metaQuery.data?.tracks ?? [];
  const regularColors = metaQuery.data?.regularColors ?? [];
  const dedicatedColors = metaQuery.data?.dedicatedColors ?? [];
  const baseExcludeColors = metaQuery.data?.baseExcludeColors ?? [];

  const termDef = React.useMemo(
    () => terms.find((term) => term.term_number === termNumber) ?? null,
    [terms, termNumber]
  );

  const classEntries = React.useMemo(() => buildAnnualClassEntries(schedule, grades), [schedule, grades]);

  const calendarQuery = useCalendarDaysQuery({
    workspaceId,
    from: termDef?.starts_on ?? '',
    to: termDef?.ends_on ?? '',
    enabled: enabled && !!termDef?.starts_on && !!termDef?.ends_on,
  });

  const timetableQuery = useAnnualTimetableCellsQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    enabled: enabled && classEntries.length > 0,
  });

  const holidaySet = React.useMemo(() => {
    const days: CalendarDay[] = calendarQuery.data ?? [];
    return new Set(
      days
        .filter((d) => d.day_kind === 'holiday' || d.day_kind === 'closure' || d.day_kind === 'vacation')
        .map((d) => d.day)
    );
  }, [calendarQuery.data]);

  const cellsByKey = React.useMemo(
    () => buildAnnualCellMap(timetableQuery.data ?? []),
    [timetableQuery.data]
  );

  const subscribedTracks = React.useMemo(
    () =>
      gradeId ? filterTracksSubscribedToGradeTrackSections(gradeTrackSections, gradeId, tracks) : [],
    [gradeTrackSections, gradeId, tracks]
  );

  React.useEffect(() => {
    if (!enabled || !workspaceId) {
      // keep-alive 비활성 시 섹션을 비우지 않음 — 복귀 시 빈 프레임/재시드 깜빡임 방지
      return;
    }
    const fromSectionCache = loadGradeTrackSectionsCache(workspaceId);
    if (fromSectionCache != null) {
      setGradeTrackSections(fromSectionCache);
    }
    let cancelled = false;
    void getGradeTrackSections(workspaceId)
      .then((rows) => {
        if (!cancelled) setGradeTrackSections(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setGradeTrackSections(fromSectionCache ?? [{ id: 'base', gradeIds: [] }]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId]);

  React.useEffect(() => {
    if (!enabled || !gradeId || !metaQuery.data) return;
    saveProgramProgressDataCache(workspaceId, academicYear, termNumber, gradeId, {
      terms,
      schedule,
      tracks: subscribedTracks,
      gradeTrackSections,
      regularColors,
      dedicatedColors,
      baseExcludeColors,
      holidayDays: Array.from(holidaySet),
      cellsByKey,
    });
  }, [
    academicYear,
    baseExcludeColors,
    cellsByKey,
    dedicatedColors,
    enabled,
    gradeId,
    gradeTrackSections,
    holidaySet,
    metaQuery.data,
    regularColors,
    schedule,
    subscribedTracks,
    termNumber,
    terms,
    workspaceId,
  ]);

  const loading =
    schoolScopedCacheSyncPending ||
    !schoolScopedCacheSyncReady ||
    metaQuery.isLoading ||
    (enabled && !!termDef?.starts_on && calendarQuery.isLoading) ||
    (enabled && classEntries.length > 0 && timetableQuery.isLoading);

  const error =
    (metaQuery.error instanceof Error ? metaQuery.error.message : metaQuery.error ? String(metaQuery.error) : null) ??
    (calendarQuery.error instanceof Error
      ? calendarQuery.error.message
      : calendarQuery.error
        ? String(calendarQuery.error)
        : null) ??
    (timetableQuery.error instanceof Error
      ? timetableQuery.error.message
      : timetableQuery.error
        ? String(timetableQuery.error)
        : null);

  return {
    loading,
    error,
    terms,
    schedule,
    tracks: subscribedTracks,
    gradeTrackSections,
    regularColors,
    dedicatedColors,
    baseExcludeColors,
    holidaySet,
    cellsByKey,
    schoolScopedCacheSyncReady,
  };
}
